import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangleIcon, MoveIcon, XIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { instancesApi } from '@nebula/shared/services'
import type { VPCListItem } from '@nebula/shared/services/vpcApi'
import { getApiErrorCode, getErrorMessage } from '@nebula/shared/utils'

/**
 * Modal "Mover VM para VPC".
 *
 * Vive fora da NetworkingPage porque é onde mora a decisão que a issue #727
 * corrigiu: mover para uma VPC sem Cloud Gateway deixa a VM sem DHCP e sem
 * internet — consequência que o cliente pode querer (laboratório, backend
 * isolado), desde que a escolha seja consciente. Aqui isso vira badge no
 * seletor + confirmação explícita, em vez de um 400 que o portal engolia.
 *
 * O aviso que a API devolve DEPOIS de mover não fica aqui: o modal fecha assim
 * que a VM é movida e entrega os avisos por `onMoved`, para a página mostrá-los
 * ao lado da topologia já atualizada. Segurar o modal aberto sobre um formulário
 * inerte (o move já aconteceu, o botão já não faz nada) lia-se como travamento.
 */

type VmParaMover = {
  id: string
  name: string
  /** Cluster da VM: VPC de outro cluster é recusada pelo backend. */
  clusterId?: string | null
}

/** O que o modal entrega à página quando a VM foi movida de fato. */
export type MoveDeVpcConcluido = {
  /** Sujeito da frase: sem ele a faixa fala de uma consequência sem dizer sobre
   *  qual das VMs da topologia ela recai. */
  vmName: string
  /** Nome confirmado pela API — não o da lista local, que pode estar velha. */
  vpcName: string
  /** Vazio no caminho feliz; com itens, o cliente PRECISA lê-los. */
  warnings: string[]
}

type ChangeNetworkModalProps = {
  vm: VmParaMover | null
  vpcs: VPCListItem[]
  onClose: () => void
  /** Obrigatório de propósito: esquecer de tratar seria perder o aviso de que a
   *  VM ficou sem DHCP e sem internet — exatamente o que a #727 evita. */
  onMoved: (resultado: MoveDeVpcConcluido) => void
}

export default function ChangeNetworkModal({
  vm,
  vpcs,
  onClose,
  onMoved,
}: ChangeNetworkModalProps) {
  const queryClient = useQueryClient()
  const [targetVpcId, setTargetVpcId] = useState('')
  const [confirmNoGateway, setConfirmNoGateway] = useState(false)

  // VPC de outro cluster nunca funciona ("Target VPC must be in the same cluster
  // as the VM"): oferecê-la é só um 400 esperando acontecer. Sem cluster
  // conhecido não dá para filtrar — melhor listar tudo do que esvaziar a lista.
  const vpcsElegiveis = useMemo(
    () =>
      vpcs.filter(
        (vpc) =>
          // `GET /vpc/list` devolve as desabilitadas; o move exige
          // `NetworkPool.enabled == True` e responderia 404 — mesma classe de
          // erro que o filtro por cluster existe para evitar.
          vpc.enabled !== false && (!vm?.clusterId || vpc.cluster_id === vm.clusterId),
      ),
    [vpcs, vm?.clusterId],
  )

  const vpcSelecionada = vpcsElegiveis.find((vpc) => vpc.id === targetVpcId)
  // `has_gateway` pode vir ausente de uma API antiga; nesse caso não há o que
  // afirmar, e inventar "sem gateway" bloquearia o fluxo por engano.
  const semGateway = vpcSelecionada?.has_gateway === false

  const changeNetworkMutation = useMutation({
    mutationFn: ({
      instanceId,
      networkId,
      confirmar,
    }: {
      instanceId: string
      networkId: string
      confirmar: boolean
    }) =>
      instancesApi.changeNetwork(instanceId, networkId, {
        confirmNoGateway: confirmar,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['end-customer-topology'] })
      queryClient.invalidateQueries({ queryKey: ['customer-vpcs'] })
      onMoved({
        vmName: data.instance?.name ?? vm?.name ?? '',
        vpcName: data.network?.name ?? vpcSelecionada?.name ?? '',
        warnings: data.warnings ?? [],
      })
      fechar()
    },
    onError: (erro) => {
      // Mesma recusa, mesma causa do Marketplace: a lista está velha. Recarregar
      // faz a `option` voltar com o sufixo "— sem gateway" na próxima abertura.
      if (getApiErrorCode(erro) === 'vpc_without_gateway') {
        queryClient.invalidateQueries({ queryKey: ['customer-vpcs'] })
      }
    },
  })

  const fechar = () => {
    setTargetVpcId('')
    setConfirmNoGateway(false)
    changeNetworkMutation.reset()
    onClose()
  }

  // O componente não desmonta ao fechar (devolve `null`): sem isto, o erro de um
  // move recusado sobreviveria à próxima abertura, sobre outra VM.
  useEffect(() => {
    setTargetVpcId('')
    setConfirmNoGateway(false)
    changeNetworkMutation.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vm?.id])

  if (!vm) return null

  const movendo = changeNetworkMutation.isPending
  const mensagemDeErro = changeNetworkMutation.isError
    ? getErrorMessage(changeNetworkMutation.error)
    : null
  // A lista pode estar velha: se o gateway foi desatachado depois do load,
  // `has_gateway` mente e o checkbox nunca apareceria — o cliente levaria o 400
  // sem saída até recarregar a página. É para isso que o backend devolve `code`
  // estruturado em vez de só texto (issue #727).
  const recusadoPorFaltaDeGateway =
    getApiErrorCode(changeNetworkMutation.error) === 'vpc_without_gateway'
  const precisaConfirmar = semGateway || recusadoPorFaltaDeGateway
  const bloqueadoPelaConfirmacao = precisaConfirmar && !confirmNoGateway

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Mover VM para VPC</h3>
          <button
            onClick={fechar}
            disabled={movendo}
            aria-label="Fechar"
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <XIcon className="size-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">VM Selecionada</label>
            <div className="rounded-lg bg-gray-50 px-3 py-2 font-medium text-gray-900">
              {vm.name}
            </div>
          </div>

          <div>
            <label htmlFor="target-vpc" className="mb-1 block text-sm font-medium text-gray-700">
              VPC de Destino
            </label>
            <select
              id="target-vpc"
              value={targetVpcId}
              onChange={(e) => {
                setTargetVpcId(e.target.value)
                // Confirmação é por VPC: trocar o destino tem de exigir uma nova.
                setConfirmNoGateway(false)
                changeNetworkMutation.reset()
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              disabled={movendo}
            >
              <option value="">Selecione uma VPC...</option>
              {vpcsElegiveis.map((vpc) => (
                <option key={vpc.id} value={vpc.id}>
                  {vpc.name} ({vpc.cidr || 'Sem CIDR'})
                  {vpc.has_gateway === false ? ' — sem gateway' : ''}
                </option>
              ))}
            </select>
            {vpcsElegiveis.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Nenhuma VPC disponível no cluster desta VM.
              </p>
            )}
          </div>

          {precisaConfirmar && (
            <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start space-x-2">
                <AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-amber-600" />
                <div className="text-sm text-amber-900">
                  <strong>{vpcSelecionada?.name}</strong> não tem Cloud Gateway conectado. Nessa VPC
                  a VM fica sem DHCP e sem acesso à internet até um gateway ser atachado — o acesso
                  volta ao mover a VM de volta para uma VPC com gateway.
                </div>
              </div>
              <label className="flex items-start space-x-2 text-sm text-amber-900">
                <input
                  type="checkbox"
                  checked={confirmNoGateway}
                  onChange={(e) => setConfirmNoGateway(e.target.checked)}
                  disabled={movendo}
                  className="mt-1"
                />
                <span>Entendo que a VM ficará sem DHCP e sem acesso à internet.</span>
              </label>
            </div>
          )}

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="text-sm text-blue-900">
              A troca acontece com a VM ligada: ela <strong>não</strong> é parada nem reiniciada —
              só a placa de rede muda de VPC. O endereço atual deixa de valer, então conexões
              abertas podem cair até a VM receber um IP da rede de destino.
            </div>
          </div>

          {mensagemDeErro && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              {mensagemDeErro}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 border-t border-gray-200 px-6 py-4">
          <button
            onClick={fechar}
            disabled={movendo}
            className="rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={() =>
              changeNetworkMutation.mutate({
                instanceId: vm.id,
                networkId: targetVpcId,
                confirmar: confirmNoGateway,
              })
            }
            disabled={!targetVpcId || movendo || bloqueadoPelaConfirmacao}
            className="flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {movendo ? (
              <>
                <div className="size-4 animate-spin rounded-full border-b-2 border-white" />
                <span>Movendo...</span>
              </>
            ) : (
              <>
                <MoveIcon className="size-4" />
                <span>Mover para VPC</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
